import { IconFa } from '@/components/wm';
import { cn } from '@/utils';
import React, { useMemo } from 'react';

import { OPPORTUNITY_STATES, opportunitySummary, opportunityUnit, scopedOpportunities } from '../utils';
import { useValueFontSize } from './useValueFontSize';

const paletteVariants = {
    primary: 'tw-bg-primary tw-text-secondary',
    secondary: 'tw-bg-secondary tw-text-gray-200',
    simple: 'tw-bg-white tw-border tw-border-solid tw-border-border tw-text-secondary'
};

const iconVariants = {
    primary: 'tw-text-secondary',
    secondary: 'tw-text-gray-200',
    simple: 'tw-text-secondary'
};

// Reads WiseMetering.opportunities, loaded once at start-up, so it does not
// update while the page is open. `sample`, when given, stands in for them: the
// type picker shows each widget with made-up values.
export default function OpportunitiesWidget({ sample, widget }) {
    const
        palette = widget.graphical.palette || 'primary',
        { measure = 'cost', siteId = '', utilityKindId = '' } = widget.data,
        unit = opportunityUnit({ measure, utilityKindId }),
        isCount = measure === 'count',
        format = value => formatValue(value, unit, 0),
        isSmallWidget = widget.graphical.size.h <= 3 || widget.graphical.size.w <= 2;

    const summary = useMemo(
        () => sample?.summary ?? opportunitySummary(scopedOpportunities({ siteId, utilityKindId }), measure),
        [measure, sample, siteId, utilityKindId]
    );

    const
        headline = format(summary.open),
        [valueRef, fontSize] = useValueFontSize(headline, isSmallWidget);

    return (
        <div
            className={cn(
                paletteVariants[palette],
                'tw-flex tw-flex-col tw-gap-1 tw-rounded-sm tw-px-4 tw-pt-4',
                'tw-h-full tw-w-full tw-overflow-hidden'
            )}
            id={widget.id}
        >
            <div>
                <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-text-base tw-font-medium tw-leading-none">{widget.title}</div>
                    <IconFa color={iconVariants[palette]} icon="savings" size="medium"/>
                </div>
                <div className="tw-text-md tw-mt-0">
                    {widget.subtitle || ''}
                </div>
            </div>
            <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-items-end tw-justify-end" ref={valueRef}>
                <div className="tw-font-bold tw-text-right tw-leading-tight" style={{ fontSize: `${fontSize}px` }}>
                    {headline}
                </div>
                <div className={cn('tw-font-medium tw-text-right tw-mt-[-6px] tw-max-w-full tw-truncate', isSmallWidget ? 'tw-text-sm' : 'tw-text-base')}>
                    {/* A count headline already says how many are open */}
                    {i18next.t(`dashboard.custom_dashboard.${isCount ? 'opportunities_open_label' : 'opportunities_open'}`, { count: summary.openCount })}
                </div>
                {!isCount && (
                    <div className="tw-text-[11px] tw-text-right tw-opacity-70 tw-mt-[-4px]">
                        {i18next.t('dashboard.custom_dashboard.opportunities_annual')}
                    </div>
                )}
            </div>
            <div className="tw-flex-shrink-0 tw-pb-3">
                <div className="tw-flex tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-sm tw-bg-black/10">
                    {summary.total > 0 && OPPORTUNITY_STATES.map(state => (
                        <div
                            key={state}
                            style={{
                                backgroundColor: WiseMetering.utils.getColorByState(state),
                                width: `${summary.totals[state] / summary.total * 100}%`
                            }}
                        />
                    ))}
                </div>
                <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-x-3 tw-text-[11px]">
                    {OPPORTUNITY_STATES.map(state => (
                        <span className="tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap" key={state}>
                            <span
                                className="tw-h-2 tw-w-2 tw-flex-shrink-0 tw-rounded-full"
                                style={{ backgroundColor: WiseMetering.utils.getColorByState(state) }}
                            />
                            {i18next.t(`states.${state}`).capitalize()} {format(summary.totals[state])}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}
